import {
  Box,
  Button,
  Container,
  Divider,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowLeftBar,
  IconBrandWhatsapp,
  IconCheck,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import GetHelpForm from "../Shared/GetHelpForm";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
  createServiceSchema,
} from "../../seo/utils";
import {
  getServicePageBySlug,
  servicePages,
} from "../../data/servicePages";
import { getToolsBySlugs } from "../../data/toolsCatalog";
import generatedBlogs from "../../data/generatedBlogs.json";
import { Blog } from "../../Model/Blog.model";
import { buildWhatsAppUrl } from "../../Services/contact.service";
import { getReviewsByServiceSlug } from "../../Services/review.service";
import { getBlogPath } from "../../utils/routes";
import ReviewsPreviewSection from "../Reviews/ReviewsPreviewSection";
import ReviewsTrustBar from "../Reviews/ReviewsTrustBar";
import "./ServiceLandingPage.css";

function ServiceLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const desktopMediaQuery = useMediaQuery(
    `(min-width: ${theme.breakpoints.md})`
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const service = getServicePageBySlug(slug);
  const whatsappUrl = buildWhatsAppUrl(
    `Hi, I need help with ${service?.primaryKeyword || "my studies"}.`
  );
  const blogs = (generatedBlogs as Blog[])
    .filter(
      (blog) =>
        blog.cluster === service?.cluster ||
        blog.targetKeyword === service?.primaryKeyword
    )
    .slice(0, 8);
  const relatedReviews = getReviewsByServiceSlug(service?.slug, 2);
  const relatedTools = getServiceToolLinks(service?.slug);

  if (!service) {
    return (
      <Container py="xl">
        <Stack gap="md">
          <Seo
            title="Service Page Not Found | SubjectBuddy"
            description="The service page you requested could not be found."
            path="/services/not-found"
            noIndex
          />
          <Title order={2}>Service page not found</Title>
          <Button variant="subtle" onClick={() => navigate("/")}>
            Back to home
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container fluid mt={desktopMediaQuery ? "md" : 50} className="service-page">
      <Seo
        title={service.title}
        description={service.metaDescription}
        path={`/services/${service.slug}`}
        keywords={[
          service.primaryKeyword,
          service.cluster.toLowerCase(),
          "academic mentoring uk",
          "student support uk",
        ]}
        schema={[
          createOrganizationSchema(),
          createServiceSchema(service),
          createFaqSchema(service.faqs),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: service.heroTitle, path: `/services/${service.slug}` },
          ]),
        ]}
      />

      <div className="service-layout">
        <Stack gap={26} className="service-main">
          <Group justify="space-between">
            <Button
              color="blue"
              variant="subtle"
              size={desktopMediaQuery ? "lg" : "sm"}
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate("/")}
              pl={0}
            >
              Back
            </Button>
            <Text size="sm" fw={600} c="dimmed">
              Target keyword: {service.primaryKeyword}
            </Text>
          </Group>

          <Paper
            radius="xl"
            p={desktopMediaQuery ? "3rem" : "1.25rem"}
            className="service-hero-card"
          >
            <Stack gap="lg">
              <Text
                tt="uppercase"
                fw={700}
                size="xs"
                c={theme.colors.blue[7]}
                style={{ letterSpacing: "0.08em" }}
              >
                {service.cluster}
              </Text>
              <Title
                order={1}
                c={theme.colors.green[9]}
                style={{
                  fontSize: desktopMediaQuery ? "3rem" : "2rem",
                  lineHeight: 1.08,
                  letterSpacing: "-0.03em",
                }}
              >
                {service.heroTitle}
              </Title>
              <Text size={desktopMediaQuery ? "lg" : "md"} c="dimmed" maw={760}>
                {service.heroDescription}
              </Text>
              <Divider />
              <Box className="service-rich-text">
                <p>{service.intro}</p>
              </Box>
              <ReviewsTrustBar compact />
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Paper withBorder radius="lg" p="lg">
                  <Stack gap="sm">
                    <Title order={3}>What this support helps with</Title>
                    <List
                      spacing="sm"
                      icon={
                        <ThemeIcon color="green" size={20} radius="xl">
                          <IconCheck size={14} />
                        </ThemeIcon>
                      }
                    >
                      {service.outcomes.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Paper>
                <Paper withBorder radius="lg" p="lg">
                  <Stack gap="sm">
                    <Title order={3}>Support focus areas</Title>
                    <List
                      spacing="sm"
                      icon={
                        <ThemeIcon color="blue" size={20} radius="xl">
                          <IconCheck size={14} />
                        </ThemeIcon>
                      }
                    >
                      {service.features.map((item) => (
                        <List.Item key={item}>{item}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <Stack gap="lg">
              <Title order={2}>Frequently Asked Questions</Title>
              {service.faqs.map((faq) => (
                <Stack key={faq.question} gap={6}>
                  <Text fw={700}>{faq.question}</Text>
                  <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                    {faq.answer}
                  </Text>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper withBorder radius="xl" p={desktopMediaQuery ? "2rem" : "1.2rem"}>
            <ReviewsPreviewSection
              title={`What students say about ${service.heroTitle}`}
              description="These review highlights keep trust signals visible inside the service flow, inspired by the review-summary and feedback sections in the reference pages."
              reviews={relatedReviews}
              compactCards
            />
          </Paper>

          {relatedTools.length ? (
            <Stack gap="md">
              <Group justify="space-between" align="end">
                <div>
                  <Title order={2}>Related Student Tools</Title>
                  <Text c="dimmed">
                    Free calculators and editors that connect with this support
                    area.
                  </Text>
                </div>
                <Button variant="subtle" component={Link} to="/tools">
                  View all tools
                </Button>
              </Group>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                {relatedTools.map((tool) => (
                  <Paper
                    key={tool.slug}
                    component={Link}
                    to={tool.url}
                    withBorder
                    radius="lg"
                    p="lg"
                    style={{
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Stack gap={8}>
                      <Text size="xs" tt="uppercase" fw={700} c="green">
                        {tool.primaryKeyword}
                      </Text>
                      <Text fw={700}>{tool.title}</Text>
                      <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                        {tool.description}
                      </Text>
                    </Stack>
                  </Paper>
                ))}
              </SimpleGrid>
            </Stack>
          ) : null}

          <Stack gap="md">
            <Group justify="space-between" align="end">
              <div>
                <Title order={2}>Related Guides</Title>
                <Text c="dimmed">
                  Internal support articles for the same keyword cluster.
                </Text>
              </div>
              <Button variant="subtle" onClick={() => navigate("/blogs")}>
                View all blogs
              </Button>
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              {blogs.map((blog) => (
                <Paper
                  key={blog.slug}
                  component={Link}
                  to={getBlogPath(blog.slug)}
                  withBorder
                  radius="lg"
                  p="lg"
                  style={{
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <Stack gap={8}>
                    <Text size="xs" tt="uppercase" fw={700} c="blue">
                      {blog.cluster}
                    </Text>
                    <Text fw={700}>{blog.title}</Text>
                    <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                      {blog.description}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Stack>

        <aside className="service-sidebar">
          <div className="service-sidebar-sticky">
            <Stack gap="md">
              <Title order={desktopMediaQuery ? 3 : 4}>Need Help With This?</Title>
              <Paper withBorder radius="lg" p={desktopMediaQuery ? "xl" : "md"}>
                <GetHelpForm formSize="sm" flatForm />
              </Paper>
              <Paper
                radius="xl"
                p={desktopMediaQuery ? "xl" : "md"}
                style={{
                  background:
                    "linear-gradient(160deg, #1f9d55 0%, #16803c 55%, #0f5132 100%)",
                  color: "#fff",
                }}
              >
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={700} tt="uppercase" size="xs">
                      WhatsApp
                    </Text>
                    <IconBrandWhatsapp size={18} />
                  </Group>
                  <Title order={desktopMediaQuery ? 3 : 4} c="white">
                    Discuss your brief directly
                  </Title>
                  <Text style={{ lineHeight: 1.7, color: "rgba(255,255,255,0.9)" }}>
                    Send your question, module brief, or deadline on WhatsApp for
                    faster guidance.
                  </Text>
                  <Button
                    color="dark"
                    radius="xl"
                    component="a"
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    leftSection={<IconBrandWhatsapp size={18} />}
                  >
                    WhatsApp us
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </div>
        </aside>
      </div>
    </Container>
  );
}

export function getServiceRoutes() {
  return servicePages.map((service) => ({
    path: service.slug,
    label: service.navLabel,
  }));
}

function getServiceToolLinks(slug?: string) {
  const linksByService: Record<string, string[]> = {
    "college-essay-editing": [
      "ai-college-essay-reviewer",
      "college-essay-editor",
    ],
    "personal-statement-help": [
      "ai-college-essay-reviewer",
      "college-essay-editor",
    ],
    "essay-editing-help": ["college-essay-editor", "speech-word-counter"],
    "speech-writing-help": ["speech-word-counter", "speech-time-calculator"],
    "gpa-improvement-assignment-help": [
      "cumulative-gpa-calculator",
      "semester-grade-calculator",
      "final-grade-calculator",
    ],
  };

  return getToolsBySlugs(slug ? linksByService[slug] || [] : []);
}

export default ServiceLandingPage;
